import {Box, Flex, Text, Title} from 'exsportia-components'
import {Formik, FormikConfig} from 'formik'
import {FC, useEffect} from 'react'
import {useQuery} from 'react-query'
import {useDispatch, useSelector} from 'react-redux'
import {object, string} from 'yup'

import {getLocationQuery} from '../../api/user/get-user-location'
import {AuthService} from '../../services/auth'
import {Button} from '../../ui/components/button/button'
import {PhoneInput} from '../../ui/components/phone-input/phone-input'
import {useLocale} from '../../utils/i18n'

type PhoneLoginFormType = {
  phone: string
}

type PhoneLoginPhoneStepProps = {
  backAction: () => void
  setPhoneNumber: (phone: string) => void
  initialPhone?: string
}

export const PhoneLoginPhoneStep: FC<PhoneLoginPhoneStepProps> = ({backAction, setPhoneNumber, initialPhone}) => {
  const {l} = useLocale()
  const dispatch = useDispatch()
  const isLoading = useSelector(AuthService.selectors.phoneLoginLoading())
  const error = useSelector(AuthService.selectors.phoneLoginError())
  const {data: location} = useQuery(getLocationQuery({}))

  // Using global persistent recaptcha managed in firebase.ts

  const handleSubmit: FormikConfig<PhoneLoginFormType>['onSubmit'] = (values) => {
    setPhoneNumber(values.phone)
    dispatch(AuthService.actions.phoneLoginSendOtpAction({phoneNumber: values.phone}))
  }

  const validationSchema = object().shape({
    phone: string().required(l('required') || 'Required'),
  })

  return (
    <Formik<PhoneLoginFormType>
      initialValues={{phone: initialPhone || ''}}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({handleSubmit: formikSubmit}) => (
        <Box pb={'24px'}>
          {/* Required anchor for Firebase invisible reCAPTCHA — renders nothing visible */}
          <div id='recaptcha-container' />

          <Flex mb='8px'>
            <Title
              text={l('enter_your_phone_number', {defaultMessage: 'Enter your phone number'})}
              styleType='h2'
            />
          </Flex>
          <Flex mb='16px'>
            <Text text={l('phoneLoginVerificationSubtitle', {defaultMessage: 'We will send a verification code via SMS'})} />
          </Flex>
          <PhoneInput
            id='phone'
            label={l('phoneNumber')}
            countryCode={(location as { country_code?: string })?.country_code?.toLowerCase() || 'us'}
          />

          {error && (
            <Box
              mt='5px'
              mb='10px'
              fontSize='12px'
              color='error'
            >
              {error}
            </Box>
          )}

          <Box mt={'16px'}>
            <Button
              isLoading={isLoading}
              // @ts-ignore
              onClick={formikSubmit}
            >
              {l('continue')}
            </Button>
          </Box>

          <Flex
            alignItems='center'
            mt='24px'
            mb='16px'
          >
            <Box
              flex={1}
              height='1px'
              bg='rgba(0, 16, 61, 0.12)'
            />
            <Box
              mx='12px'
              color='text-secondary'
              fontSize='14px'
            >
              {l('or', {defaultMessage: 'or'})}
            </Box>
            <Box
              flex={1}
              height='1px'
              bg='rgba(0, 16, 61, 0.12)'
            />
          </Flex>
          <Button
            type='flat'
            onClick={backAction}
            disabled={isLoading}
          >
            {l('continueWithEmail', {defaultMessage: 'Continue with email'})}
          </Button>
        </Box>
      )}
    </Formik>
  )
}
